import { TreeBoxIcon, TreeChainIcon, TreeFolderIcon, TreeUsersIcon } from '@/shared/ui';
import type { TreeData, TreeLabelSegment, TreeNodeType } from '@/shared/ui';
import type {
  GetColumbusTreeResponse,
  DestinationSeasonNode,
  ColumbusProductNode,
  TermsSerialNode,
} from '@/api/generated/schemas';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function parseLocalDate(dateStr: string): Date {
  const datePart = dateStr.split('T')[0]!;
  const [y, m, d] = datePart.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

function addDaysToDate(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatShort(d: Date): string {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

function formatFull(d: Date): string {
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/**
 * Termínový řádek dle PHX-TREE-01: číslo (medium) · (den) tlumeně · datum/rozsah
 * sekundárně s tabulkovými číslicemi.
 */
function buildTermSegments(
  name: string,
  dateFrom: string,
  nights: number | null,
  locale: string,
  formatNights: (n: number) => string
): TreeLabelSegment[] {
  const from = parseLocalDate(dateFrom);
  const day = from.toLocaleDateString(locale, { weekday: 'short' });
  const range =
    nights != null && nights > 0
      ? `${formatShort(from)} – ${formatFull(addDaysToDate(from, nights))} (${formatNights(nights)})`
      : formatShort(from);
  return [
    { text: name, variant: 'strong' },
    { text: `(${day})`, variant: 'muted' },
    { text: range, variant: 'data' },
  ];
}

export type ColumbusNodeType = 'destinationSeason' | 'product' | 'termsSerial' | 'term';

export const COLUMBUS_NODE_TYPES = [
  'destinationSeason',
  'product',
  'termsSerial',
  'term',
] as const satisfies readonly ColumbusNodeType[];

export interface ColumbusNodeDescriptor {
  type: ColumbusNodeType;
  destinationSeason?: DestinationSeasonNode;
  product?: ColumbusProductNode;
  termsSerial?: TermsSerialNode;
  productId?: number;
  termsSerialId?: number;
}

export interface ColumbusTreeLabels {
  destinationSeason: string;
  product: string;
  termsSerial: string;
  term: string;
  formatNights: (n: number) => string;
}

function dsNodeId(dsId: number): string {
  return `ds:${dsId}`;
}

function productNodeId(dsId: number, productId: number): string {
  return `product:${dsId}:${productId}`;
}

function tsNodeId(dsId: number, productId: number, tsId: number): string {
  return `ts:${dsId}:${productId}:${tsId}`;
}

function termNodeId(dsId: number, productId: number, tsId: number, termId: number): string {
  return `term:${dsId}:${productId}:${tsId}:${termId}`;
}

export function buildColumbusTreeData(
  response: GetColumbusTreeResponse,
  labels: ColumbusTreeLabels,
  locale: string
): TreeData {
  const nodesById: TreeData['nodesById'] = {};
  const rootIds: string[] = [];

  for (const ds of response.destinationSeasons) {
    const dsId = dsNodeId(ds.id);
    rootIds.push(dsId);

    const productNodeIds = ds.products.map((p) => productNodeId(ds.id, p.productId));

    nodesById[dsId] = {
      id: dsId,
      label: ds.name,
      type: 'destinationSeason',
      parentId: null,
      childIds: productNodeIds,
    };

    for (const product of ds.products) {
      const pId = productNodeId(ds.id, product.productId);
      const tsNodeIds = product.termsSerials.map((ts) => tsNodeId(ds.id, product.productId, ts.id));

      nodesById[pId] = {
        id: pId,
        label: product.name,
        type: 'product',
        parentId: dsId,
        childIds: tsNodeIds,
      };

      for (const ts of product.termsSerials) {
        const tsId = tsNodeId(ds.id, product.productId, ts.id);
        const termNodeIds = ts.terms.map((t) => termNodeId(ds.id, product.productId, ts.id, t.id));

        nodesById[tsId] = {
          id: tsId,
          label: ts.name,
          // Série má dle anatomie medium weight
          labelSegments: [{ text: ts.name, variant: 'strong' }],
          type: 'termsSerial',
          parentId: pId,
          childIds: termNodeIds,
        };

        for (const term of ts.terms) {
          const tId = termNodeId(ds.id, product.productId, ts.id, term.id);
          const segments = buildTermSegments(
            term.name,
            term.dateFrom,
            term.nights,
            locale,
            labels.formatNights
          );
          nodesById[tId] = {
            id: tId,
            label: segments.map((s) => s.text).join(' '),
            labelSegments: segments,
            type: 'term',
            parentId: tsId,
            childIds: [],
          };
        }
      }
    }
  }

  // Ikony dle PHX-TREE-01 (barvy úrovní dodává TreeView podle hloubky)
  const typeDefinitions: TreeNodeType[] = [
    { id: 'destinationSeason', label: labels.destinationSeason, icon: TreeFolderIcon },
    { id: 'product', label: labels.product, icon: TreeBoxIcon },
    { id: 'termsSerial', label: labels.termsSerial, icon: TreeChainIcon },
    { id: 'term', label: labels.term, icon: TreeUsersIcon },
  ];

  return { nodesById, rootIds, typeDefinitions, actionsByType: [] };
}

/** Vrátí ID destinace/sezóny z nodeId formátu `ds:<id>`, jinak null. */
export function parseDsNodeId(nodeId: string): number | null {
  const match = /^ds:(\d+)$/.exec(nodeId);
  return match ? Number(match[1]) : null;
}

/** Vrátí productId z nodeId formátu `product:<dsId>:<productId>`, jinak null. */
export function parseProductNodeId(nodeId: string): number | null {
  const match = /^product:\d+:(\d+)$/.exec(nodeId);
  return match ? Number(match[1]) : null;
}

/** Vrátí { dsId, productId } z nodeId formátu `product:<dsId>:<productId>`, jinak null. */
export function parseFullProductNodeId(nodeId: string): { dsId: number; productId: number } | null {
  const match = /^product:(\d+):(\d+)$/.exec(nodeId);
  if (!match) return null;
  return { dsId: Number(match[1]), productId: Number(match[2]) };
}

/**
 * Parses node ID back to entity references.
 * Returns null if node is not a termsSerial node (only those open the planner).
 */
export function parseTsNodeId(
  nodeId: string
): { destinationSeasonId: number; productId: number; termsSerialId: number } | null {
  const match = /^ts:(\d+):(\d+):(\d+)$/.exec(nodeId);
  if (!match) return null;
  return {
    destinationSeasonId: Number(match[1]),
    productId: Number(match[2]),
    termsSerialId: Number(match[3]),
  };
}

export function parseTermNodeId(nodeId: string): {
  destinationSeasonId: number;
  productId: number;
  termsSerialId: number;
  termId: number;
} | null {
  const match = /^term:(\d+):(\d+):(\d+):(\d+)$/.exec(nodeId);
  if (!match) return null;
  return {
    destinationSeasonId: Number(match[1]),
    productId: Number(match[2]),
    termsSerialId: Number(match[3]),
    termId: Number(match[4]),
  };
}

export function getAncestorIds(treeData: TreeData, nodeId: string): string[] {
  const ancestors: string[] = [];
  let current = treeData.nodesById[nodeId];
  while (current?.parentId) {
    ancestors.push(current.parentId);
    current = treeData.nodesById[current.parentId];
  }
  return ancestors;
}

export function expandToNode(
  treeData: TreeData,
  nodeId: string,
  base: Iterable<string> = []
): Set<string> {
  const result = new Set(base);
  for (const ancestorId of getAncestorIds(treeData, nodeId)) {
    result.add(ancestorId);
  }
  return result;
}
